import "./index.css";

import React from "react";
import PageDataComponent from "Core/components/PageDataComponent";
import {connect} from "react-redux";
import {getPageActions} from "Core/helpers/redux";
import {selectors} from "Core/store/reducers";
import * as appConfig from "../config";
import * as pageConfig from "./config";
import * as actions from "./actions";
import {getMenuSidebarShrankFromStorage} from "Layout/elements/MainSidebar/helpers";
import {reducerStoreKey} from "./reducer";
import {areAllObjectPropsEmpty, getObject, getString, getStringForDisplay, isset} from 'Core/helpers/data';
import * as filterDataMap from "./dataMap/filter";
import {scrollToSelector} from "Core/helpers/dom";
import {getPopupInstance} from "Core/helpers/popup";
import ItemPopup from "./popups/ItemPopup";
import {Tooltip} from "Core/components/global/Tooltip";
import Button, {BUTTON_DISPLAY_TYPE, BUTTON_STYLE} from "Core/components/display/Button";
import Label from "Core/components/display/Label";
import SimpleStaticSearch, {
	DynamicPropFunction,
	SIMPLE_STATIC_SEARCH_DISPLAY_TYPE,
	SIMPLE_STATIC_SEARCH_LAYOUT,
	SimpleStaticSearchOptionObject
} from 'Core/components/advanced/SimpleStaticSearch';
import DataTable, {DATA_TABLE_CELL_TYPE, DATA_TABLE_CELL_TYPE_DATE} from 'Core/components/advanced/DataTable';
import {PAGINATION_TYPE} from "Core/components/action/Pagination";
import {AsyncMountError} from "Core/errors";
import {
	DataTableCellAnyTypeOptionsDataObject,
	DataTableCellDateTypeOptionsDataObject
} from 'Core/components/advanced/DataTable/DataTableCell/dataObjects';
import ProjectSelectInput from 'Components/input/ProjectSelectInput';
import CampaignSelectInput from 'Components/input/CampaignSelectInput';
import MessageDefinitionSelectInput from 'Components/input/MessageDefinitionSelectInput';
import {
	CAMPAIGN_MESSAGE_CONTENT_TYPE,
	CAMPAIGN_MESSAGE_CONTENT_TYPE_ICONS,
	CAMPAIGN_MESSAGE_CONTENT_TYPES
} from 'Pages/apps/default/projectPages/campaign/const';
import {CUSTOMER_MESSAGE_STATUS_CLASS_MAP, CUSTOMER_MESSAGE_STATUSES} from 'Pages/apps/default/const';
import IconOptionComponent from 'Core/components/input/SelectInput/options/IconOption';
import IconSingleValueComponent from 'Core/components/input/SelectInput/singleValues/IconSingleValue';
import {SelectDynamicValueFunction} from 'Core/components/input/SelectInput';
import {STANDARD_DATE_TIME_FORMAT} from 'Core/const/datetime';
import Icon from 'Core/components/display/Icon';
import {getAppLocaleDateFormat, getAppLocaleDatetimeFormat} from 'Core/helpers/locale';
import {LOCALE_DATE_FORMAT_NAME, LOCALE_TIME_FORMAT_NAME} from 'Core/const/locale';
import {get, pick} from 'lodash';
import {getAuthorizedUrl} from 'Core/helpers/url';
import {endOfDay, endOfWeek, startOfDay, startOfWeek} from 'date-fns';

/**
 * Redux 'mapStateToProps' function
 *
 * @param {object} state - Redux entire store state.
 * @return {Object<string, any>} Mapped props that can be used in component.
 */
const mapStateToProps = state => ({
	isMobileBreakpoint: selectors.breakpoint.isMobileBreakpoint(state),
	mainSidebarShrank: getMenuSidebarShrankFromStorage(selectors.mainSidebar.shrank(state)),
	mainList: selectors[reducerStoreKey].getMessagesList(state),
	mainListPagination: selectors[reducerStoreKey].getMessagesListPagination(state),
	mainListSort: selectors[reducerStoreKey].getMessagesListSort(state),
	mainListFilter: selectors[reducerStoreKey].getMessagesListFilter(state),
	singleProject: selectors['projectSelector'].getSingleProject(state),
});

class MessagesPage extends PageDataComponent {
	constructor(props) {
		super(props, {
			data: {
				/**
				 * Flag showing if filter is loading
				 */
				filterLoading: false,

				/**
				 * Flag showing if page is loading data
				 * @type {boolean}
				 */
				loading: false,
			},

			/**
			 * Flag that specifies if main data table height will be limited to the available space
			 */
			limitToAvailableSpace: false
		}, {
			domPrefix: 'messages-page',
			translationPath: pageConfig.translationPath,
			routerPath: pageConfig.routerPath,
			checkLogin: false,
			disableLoad: true,
		}, 'page_title');

		// Refs
		this.mainListFilterRef = null;

		// Data methods
		this.getFilterOutputData = this.getFilterOutputData.bind(this);
		this.reloadMainList = this.reloadMainList.bind(this);
		this.loadMainListPage = this.loadMainListPage.bind(this);
		this.sortMainList = this.sortMainList.bind(this);
		this.filterMainList = this.filterMainList.bind(this);
		this.removeMainListFilter = this.removeMainListFilter.bind(this);
		this.isMainListFilterEmpty = this.isMainListFilterEmpty.bind(this);

		// Popup methods
		this.openMainListItemPopup = this.openMainListItemPopup.bind(this);
		this.closeMainListItemPopup = this.closeMainListItemPopup.bind(this);
		this.handleMainListItemPopupClose = this.handleMainListItemPopupClose.bind(this);

		// Render methods
		this.renderActions = this.renderActions.bind(this);
	}


	// Component property methods ---------------------------------------------------------------------------------------
	/**
	 * Get component's ID that can be used as DOM element id attribute value
	 * @return {string}
	 */
	getDomId() { return this.getOption('domPrefix'); }


	// Data methods -----------------------------------------------------------------------------------------------------
	/**
	 * Method that will be called on component mount and should be used to load any data required by the page
	 * @return {Promise<any>}
	 * @throws {AsyncMountError}
	 */
	loadPageData() {
		const {mainList, loadMessagesListAction} = this.props;

		// If main list was already loaded (user already opened the page before)
		if (isset(mainList)) {
			// Open filter if it is not empty
			if (!this.isMainListFilterEmpty() && this.mainListFilterRef) this.mainListFilterRef.open();
			// Reload main list with currently applied filter, sort and pagination
			return this.reloadMainList()
				.then(res => { if (!isset(res)) throw new AsyncMountError(); })
				.then(() => setTimeout(() => scrollToSelector('#main-page-table', false, 80)));
		}
		// Load main list if it is not already loaded
		else {
			return this.setValue('loading', true)
				.then(() => this.executeAbortableActionMount(loadMessagesListAction, this.getFilterOutputData()))
				.then(() => this.setValue('loading', false))
				.then(() => setTimeout(() => scrollToSelector('#main-page-table', false, 80)));
		}
	}

	/**
	 * Get filter data mapped for output
	 * @note This method adds default filters.
	 * 
	 * @param {?Object} [filter] - Filter to map for output.
	 * @return {*}
	 */
	getFilterOutputData(filter) {
		return filterDataMap.output(
			areAllObjectPropsEmpty(pick(filter, [
				'scheduledDateFrom', 'scheduledDateTo', 'deliveryDateFrom', 'deliveryDateTo'
			]), true, true, false) ?
				// Add default scheduled date filter values since we must always have at least one date related filter 
				// applied for performance reasons (API returns an error otherwise!)
				{...getObject(filter), scheduledDateFrom: startOfDay(new Date()), scheduledDateTo: endOfDay(new Date())} :
				filter
		);
	}

	/**
	 * Reload main list using current options (page, sort, ...)
	 * @return {Promise<*>}
	 */
	reloadMainList() {
		const {loadMessagesListAction, mainListPagination, mainListSort, mainListFilter} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = this.getFilterOutputData(mainListFilter);

		return this.executeAbortableAction(loadMessagesListAction, oFilter, pageNo, perPage, sortBy, sortDir)
			.then(res => {
				this.mainListFilterRef?.reload();
				return res;
			});
	}

	/**
	 * Reload main list using current options (page, sort, ...) if any
	 * @param {number} [pageNo=1] - Page number to load (starts with 1).
	 * @return {Promise<*>}
	 */
	loadMainListPage(pageNo = 1) {
		const {loadMessagesListAction, mainListPagination, mainListSort, mainListFilter} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = this.getFilterOutputData(mainListFilter);

		return this.executeAbortableAction(loadMessagesListAction, oFilter, pageNo, perPage, sortBy, sortDir);
	}

	/**
	 * Sort main list
	 * @param {string} sortBy - Name of the sort column.
	 * @param {string} sortDir - Direction of the sort.
	 * @return {Promise<*>}
	 */
	sortMainList(sortBy, sortDir) {
		const {loadMessagesListAction, mainListPagination, mainListFilter} = this.props;
		const {pageNo, perPage} = mainListPagination;
		const oFilter = this.getFilterOutputData(mainListFilter);

		return this.executeAbortableAction(loadMessagesListAction, oFilter, pageNo, perPage, sortBy, sortDir);
	}

	/**
	 * Filter main list
	 * @param {Object} filter - Filter object where keys are filter field names and values are filter values.
	 * @return {Promise<*>}
	 */
	filterMainList(filter) {
		const {
			loadMessagesListAction, setMessagesFilterAction, mainListPagination, mainListSort,
		} = this.props;
		const {perPage} = mainListPagination;
		const {sortBy, sortDir} = mainListSort;
		const oFilter = this.getFilterOutputData(filter);
		
		// Set filter so that the change will be detected after IO
		setMessagesFilterAction(filter);

		return this.setValue('filterLoading', true)
			.then(() => this.executeAbortableAction(loadMessagesListAction, oFilter, 1, perPage, sortBy, sortDir))
			.then(() => this.setValue('filterLoading', false))
			.then(() => {
				if (areAllObjectPropsEmpty(oFilter, true, false)) {
					if (this.mainListFilterRef) {
						this.mainListFilterRef.close()
							.then(() => this.setState({limitToAvailableSpace: true}));
					}
				} else {
					scrollToSelector('#main-page-table', false, 80);
				}
			});
	}

	/**
	 * Remove main list filter
	 * @return {Promise<*>}
	 */
	removeMainListFilter() {
		return this.filterMainList(null);
	}

	/**
	 * Check if main list filter is applied
	 * @return {Boolean}
	 */
	isMainListFilterEmpty() {
		return areAllObjectPropsEmpty(this.getProp('mainListFilter'), true, false);
	}


	// Router methods ---------------------------------------------------------------------------------------------------
	/**
	 * Method that will be called if current URL matches the 'item' sub-url of the page
	 * @note Item sub-url uses '/item' router path and 'id' as router path param ('/item/:id') on top of to the router
	 * path of the page (see 'options.routerPath').
	 *
	 * @param {string|number} id - Item ID.
	 * @param {Object} [prevLocation] - Previous router location.
	 * @return {string|Promise<string>} GUI ID of the component (popup, dialog, ...) that is rendered when page is on
	 * 'item' sub-url if such component exists.
	 */
	handleItemUrl(id, prevLocation) {
		const {loadMessagesItemAction} = this.props;

		// @note This is done to avoid opening multiple dialogs. Dynamic URL component data should be changes using the 
		// standard 'updateDynamics' method.
		if (!this.urlComponentGUIID) {
			this.urlComponentGUIID = this.openMainListItemPopup();
		}
		// If URL component is opened, update its dynamic items (tabs, action buttons, ...)
		else {
			const mainListItemPopup = getPopupInstance(this.urlComponentGUIID);
			mainListItemPopup.updateDynamics().then();
		}

		// Try to load main list item
		return new Promise(resolve =>
			// Timeout is added to allow for the popup open animation to finish
			setTimeout(() => resolve(
				this.executeAbortableAction(loadMessagesItemAction, id)
					.then(mainListItem => {
						// If main list item is successfully loaded
						if (mainListItem) {
							return this.urlComponentGUIID;
						}
						// If main list item could not be loaded (usually if item with ID from URL does not exist)
						else {
							// Close main list item popup if it is opened
							if (this.urlComponentGUIID) this.closeUrlComponent();
							// Redirect to page base url (removes main list item ID from URL if it exists)
							this.redirectToBase();
							return '';
						}
					})
			))
		);
	}

	/**
	 * Method that will be called if current URL matches the base URL of the page
	 *
	 * @param {Object} [prevLocation] - Previous router location.
	 * @return {string|Promise<string>} GUI ID of the component (popup, dialog, ...) that is rendered when page is on its
	 * base URL if such component exists.
	 */
	handleBaseUrl(prevLocation) {
		this.closeMainListItemPopup();
		return '';
	}

	/**
	 * Method that will be called when page component unmounts and should handle closing of any page url or sub-url
	 * component if it exists.
	 */
	closeUrlComponent() {
		this.closeMainListItemPopup();
	}


	// Popup methods ----------------------------------------------------------------------------------------------------
	/**
	 * Open main list item popup
	 */
	openMainListItemPopup() {
		const {openPopupAction} = this.props;
		return openPopupAction(ItemPopup, {
			onClose: this.handleMainListItemPopupClose,
		});
	}

	/**
	 * Close main list item popup
	 */
	closeMainListItemPopup() {
		const {closePopupAction, clearMessagesItemAction} = this.props;

		// Close item popup
		const urlComponentGUIID = this.getUrlComponentGUIID();
		if (urlComponentGUIID) {
			closePopupAction(urlComponentGUIID);
			this.clearUrlComponentGUIID();
		}

		// Clear popup Redux data
		clearMessagesItemAction();
	}

	/**
	 * Handle main list item popup 'onClose' event
	 * @return {void}
	 */
	handleMainListItemPopupClose() {
		this.clearUrlComponentGUIID();
		this.redirectToBase();
		// Timeout is added to allow for the popup close animation to finish
		setTimeout(this.reloadMainList);
	}
	
	
	// Render methods ---------------------------------------------------------------------------------------------------
	/**
	 * Render data table actions cell
	 * @param {MessagesListItemDataObject} row - Data table row.
	 * @return {JSX.Element}
	 */
	renderActions(row) {
		return (
			<div className="actions standard-actions-row">
				<Tooltip
					tag="div"
					title={this.t('view_tooltip')}
					size="small"
					position="top-center"
					arrow={true}
					interactive={false}
					touchHold={true}
               delay={250}
				>
					<Button
						className="action-btn no-border-radius"
						displayStyle={BUTTON_STYLE.ACTION}
						displayType={BUTTON_DISPLAY_TYPE.NONE}
						icon="paper-plane"
						onClick={() => this.redirectToItem(row.messageId)}
					/>
				</Tooltip>
			</div>
		);
	}

	/**
	 * Render page title
	 * @description This method specifies how page title will be rendered if page title should be rendered. It does not
	 * determine if page title should be rendered.
	 * @return {JSX.Element}
	 */
	renderPageTitle() {
		const {title} = this.state;

		return (
			<h1 className="page-title with-actions reload-only">
				<div className="content">{title ? this.translate(title, this.titlePathPrefix) : ''}</div>
				<div className="actions">
					<div className="action-button main-button">
						<Tooltip
							tag="div"
							title={this.t('Reload data', 'general')}
							size="small"
							position="top-center"
							arrow={true}
							interactive={false}
							touchHold={true}
							delay={250}
						>
							<Button
								big={true}
								icon="refresh"
								displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
								displayStyle={BUTTON_STYLE.SUBTLE}
								onClick={this.reloadMainList}
							/>
						</Tooltip>
					</div>
				</div>
			</h1>
		);
	}
	
	render() {
		const {
			isMobileBreakpoint, mainList, mainListPagination, mainListSort, mainListFilter, mainSidebarShrank, 
			toggleMainSidebarSizeAction, singleProject
		} = this.props;
		const {limitToAvailableSpace} = this.state;
		
		let filterOptions = [
			new SimpleStaticSearchOptionObject(
				'campaignId',
				this.t('campaignIdField'),
				SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.CUSTOM,
				{
					isClearable: true,
					projectId: new DynamicPropFunction(currentFilter => getString(currentFilter, 'projectId')),
				},
				CampaignSelectInput,
				undefined,
				undefined,
				'projectId'
			),
			new SimpleStaticSearchOptionObject(
				'messageDefinitionId',
				this.t('messageDefinitionIdField'),
				SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.CUSTOM,
				{
					isClearable: true,
					campaign: new DynamicPropFunction(currentFilter => ({
						id: getString(currentFilter, 'campaignId'), 
						name: 'Ping'
					})),
					optionLabelSeparator: ' #'
				},
				MessageDefinitionSelectInput,
				undefined,
				null,
				'campaignId'
			),
			new SimpleStaticSearchOptionObject(
				'messageType',
				this.t('messageTypeField'),
				SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.SELECT,
				{
					isClearable: true,
					options: CAMPAIGN_MESSAGE_CONTENT_TYPES
						.map(t => ({label: this.t(t, 'constants.messageType'), value: t})),
					components: {Option: IconOptionComponent, SingleValue: IconSingleValueComponent},
					labelProps: {
						symbolPrefix: new SelectDynamicValueFunction(data =>
							getString(CAMPAIGN_MESSAGE_CONTENT_TYPE_ICONS, [data.value, 'symbolPrefix'])
						),
						symbol: new SelectDynamicValueFunction(data =>
							getString(CAMPAIGN_MESSAGE_CONTENT_TYPE_ICONS, [data.value, 'symbol'])
						),
					},
					singleValueProps: {
						symbolPrefix: new SelectDynamicValueFunction(data =>
							getString(CAMPAIGN_MESSAGE_CONTENT_TYPE_ICONS, [data.value, 'symbolPrefix'])
						),
						symbol: new SelectDynamicValueFunction(data =>
							getString(CAMPAIGN_MESSAGE_CONTENT_TYPE_ICONS, [data.value, 'symbol'])
						),
					}
				}
			),
			new SimpleStaticSearchOptionObject(
				'externalCrmId',
				this.t('externalCrmIdField')
			),
			new SimpleStaticSearchOptionObject(
				'mobilePhoneNumber',
				this.t('phoneField'),
			),
			new SimpleStaticSearchOptionObject(
				'email',
				this.t('emailField')
			),
			new SimpleStaticSearchOptionObject(
				'messageStatus',
				this.t('messageStatusField'),
				SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.SELECT,
				{
					isClearable: true,
					options: CUSTOMER_MESSAGE_STATUSES.map(i => ({
						label: this.t(i, 'constants.customerMessageStatus'), 
						value: i
					})),
				}
			),
			new SimpleStaticSearchOptionObject(
				'scheduledDateFrom',
				this.t('scheduledDateFromField'),
				SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATETIME,
				{
					valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S,
				},
			),
			new SimpleStaticSearchOptionObject(
				'scheduledDateTo',
				this.t('scheduledDateToField'),
				SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATETIME,
				{
					valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S,
				},
			),
			new SimpleStaticSearchOptionObject(
				'deliveryDateFrom',
				this.t('deliveryDateFromField'),
				SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATETIME,
				{
					valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S,
				},
			),
			new SimpleStaticSearchOptionObject(
				'deliveryDateTo',
				this.t('deliveryDateToField'),
				SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.DATETIME,
				{
					valueFormat: STANDARD_DATE_TIME_FORMAT.ISO_DATE_TIME_S,
				},
			),
		];
		if (!singleProject) {
			filterOptions.unshift(new SimpleStaticSearchOptionObject(
				'projectId',
				this.t('projectIdField'),
				SIMPLE_STATIC_SEARCH_DISPLAY_TYPE.CUSTOM,
				{
					isClearable: true,
					useGlobalProjectList: true,
					autoSelectSingleProject: true,
				},
				ProjectSelectInput,
			));
		}
		
		return this.renderLayout((
			<div id={this.getDomId()} className={`${this.getOption('domPrefix')}`}>
				{
					this.hasTranslation('page_short_description') && this.t('page_short_description') ?
						<div className="simple-page-description">
							<Label content={this.t('page_short_description')} supportHtml={true} />
						</div>
						: null
				}

				<SimpleStaticSearch
					styleName="compact"
					className={`main-search rows-6`}
					defaultCollapse={false}
					layout={SIMPLE_STATIC_SEARCH_LAYOUT.STACKED}
					buttonProps={{
						displayStyle: BUTTON_STYLE.DEFAULT
					}}
					options={filterOptions}
					value={mainListFilter}
					title={(<Label icon="search" content={this.t('Search', 'general')} />)}
					applied={!this.isMainListFilterEmpty()}
					enableToolbar={true}
					enableResetButton={false}
					showAdditionalToolbar={true}
					additionalToolbarContent={_sss => (
						<div className="additional-search-buttons">
							<Button 
								icon="calendar" 
								label={this.t('scheduledTodaySearch')}
								displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
								displayStyle={BUTTON_STYLE.SUBTLE}
								onClick={() => {
									_sss.setState(prevState => ({
										data: {
											...prevState.data,
											scheduledDateFrom: startOfDay(new Date()),
											scheduledDateTo: endOfDay(new Date()),
											deliveryDateFrom: null,
											deliveryDateTo: null,
										},
									}))
										.then(() => _sss.update());
								}} 
							/>
							<Button 
								icon="calendar" 
								label={this.t('scheduledThisWeekSearch')}
								displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
								displayStyle={BUTTON_STYLE.SUBTLE}
								onClick={() => {
									_sss.setState(prevState => ({
										data: {
											...prevState.data,
											scheduledDateFrom: startOfWeek(new Date(), {weekStartsOn: 1}),
											scheduledDateTo: endOfWeek(new Date(), {weekStartsOn: 1}),
											deliveryDateFrom: null,
											deliveryDateTo: null,
										},
									}))
										.then(() => _sss.update());
								}}
							/>
							<Button 
								icon="calendar" 
								label={this.t('deliveredTodaySearch')}
								displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
								displayStyle={BUTTON_STYLE.SUBTLE}
								onClick={() => {
									_sss.setState(prevState => ({
										data: {
											...prevState.data,
											scheduledDateFrom: null,
											scheduledDateTo: null,
											deliveryDateFrom: startOfDay(new Date()),
											deliveryDateTo: endOfDay(new Date()),
										},
									}))
										.then(() => _sss.update());
								}}
							/>
							<Button 
								icon="calendar" 
								label={this.t('deliveredThisWeekSearch')}
								displayType={BUTTON_DISPLAY_TYPE.TRANSPARENT}
								displayStyle={BUTTON_STYLE.SUBTLE}
								onClick={() => {
									_sss.setState(prevState => ({
										data: {
											...prevState.data,
											scheduledDateFrom: null,
											scheduledDateTo: null,
											deliveryDateFrom: startOfWeek(new Date(), {weekStartsOn: 1}),
											deliveryDateTo: endOfWeek(new Date(), {weekStartsOn: 1}),
										},
									}))
										.then(() => _sss.update());
								}}
							/>
						</div>
					)}
					onChange={this.filterMainList}
					onRemove={this.removeMainListFilter}
					onToggle={visible => this.setState({limitToAvailableSpace: visible})}
					ref={node => { this.mainListFilterRef = node; }}
				/>

				<DataTable
					id="main-page-table"
					className="standard sticky-last-column lh-1-2"
					responsive={true}
					responsiveBreakpointName="bp-m"
					limitToAvailableSpace={limitToAvailableSpace && !isMobileBreakpoint}
					highlightOnHover={true}
					columns={[
						{
							name: 'messageType',
							label: this.t('messageTypeColumn'),
							dataType: DATA_TABLE_CELL_TYPE.ANY,
							dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
								content: row => (
									<div className="text-center">
										{!!getString(CAMPAIGN_MESSAGE_CONTENT_TYPE_ICONS, row.messageType) ?
											<div style={{fontSize: '1.5em'}}>
												{!!CAMPAIGN_MESSAGE_CONTENT_TYPE_ICONS[row.messageType] ?
													<Icon {...CAMPAIGN_MESSAGE_CONTENT_TYPE_ICONS[row.messageType]} /> :
													<Icon symbol="bell" />
												}
											</div> 
											:
											null
										}
										<Label
											element="div"
											elementProps={{className: 'no-wrap'}}
											content={this.t(row.messageType, 'constants.messageTypeShort')}
										/>
									</div>
								)
							}),
							defaultValue: '—',
							emptyAsDefault: true,
							width: 1,
						},
						{
							name: 'recipient',
							label: this.t('recipientColumn'),
							tooltip: this.t('recipientColumnTooltip'),
							dataType: DATA_TABLE_CELL_TYPE.ANY,
							dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
								/**
								 * @param {MessagesListItemDataObject} row
								 * @return {JSX.Element}
								 */
								content: row => (
									<a 
										href={getAuthorizedUrl(`customers/item/${row.customer.id}`)} 
										target="_blank" 
										rel="noopener noreferrer"
									>
										<small className="opacity-75">
											<Label content={this.t('customerExternalCrmIdLabel')} />:&nbsp;
											<Label content={getStringForDisplay(get(row, 'customer.crmId'),undefined,true,true)}/>
										</small>
										<br />
										{
											[
												CAMPAIGN_MESSAGE_CONTENT_TYPE.SMS, 
												CAMPAIGN_MESSAGE_CONTENT_TYPE.VIBER,
												CAMPAIGN_MESSAGE_CONTENT_TYPE.PUSH_NOTIFICATION,
											].includes(row.messageType) ?
												<>
													<div>
														<Label element="strong" content={getStringForDisplay(get(row, 'recipient'))}/>
													</div>
													<div className="opacity-75">
														<Label 
															element="small" 
															content={getStringForDisplay(get(row, 'customer.email'))} 
														/>
													</div>
												</>
											: [
												CAMPAIGN_MESSAGE_CONTENT_TYPE.EMAIL,
											].includes(row.messageType) ?
												<>
													<div>
														<Label 
															element="strong" 
															content={getStringForDisplay(get(row, 'customer.email'))} 
														/>
													</div>
													<div className="opacity-75">
														<Label element="small" content={getStringForDisplay(get(row, 'recipient'))} />
													</div>
												</>
											: 
												<>
													<div className="opacity-75">
														<Label element="small" content={getStringForDisplay(get(row, 'recipient'))}/>
													</div>
													<div className="opacity-75">
														<Label 
															element="small" 
															content={getStringForDisplay(get(row, 'customer.email'))} 
														/>
													</div>
												</>
										}
									</a>
								),
							}),
							defaultValue: '—',
							emptyAsDefault: true,
							width: 1,
							stopPropagation: true,
						},
						{
							name: 'clientName',
							label: this.t('clientNameColumn'),
							defaultValue: '—',
							emptyAsDefault: true,
						},
						{
							name: 'creator',
							label: this.t('creatorColumn'),
							defaultValue: '—',
							emptyAsDefault: true,
						},
						{
							name: 'status',
							label: this.t('statusColumn'),
							dataType: DATA_TABLE_CELL_TYPE.ANY,
							dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
								content: row => (
									<Label 
										element="span"
										elementProps={{
											className: `status no-wrap tag ${CUSTOMER_MESSAGE_STATUS_CLASS_MAP[row.status]}`
										}}
										content={this.t(row.status, 'constants.customerMessageStatus')}
										help={this.t(row.status, 'constants.customerMessageStatusDescription')}
									/>
								),
							}),
							width: 1,
						},
						{
							name: 'creationDate',
							label: this.t('creationDateColumn'),
							dataType: DATA_TABLE_CELL_TYPE_DATE,
							dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
								outputFormat: getAppLocaleDateFormat(LOCALE_DATE_FORMAT_NAME.SHORT),
								whiteSpace: 'nowrap', 
							}),
							defaultValue: '—',
							emptyAsDefault: true,
							width: 1,
						},
						{
							name: 'scheduledDate',
							label: this.t('scheduledDateColumn'),
							dataType: DATA_TABLE_CELL_TYPE_DATE,
							dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
								outputFormat: getAppLocaleDatetimeFormat(
									LOCALE_DATE_FORMAT_NAME.SHORT,
									LOCALE_TIME_FORMAT_NAME.STANDARD
								),
							}),
							defaultValue: '—',
							emptyAsDefault: true,
							width: 1,
						},
						{
							name: 'deliveryDate',
							label: this.t('deliveryDateColumn'),
							dataType: DATA_TABLE_CELL_TYPE_DATE,
							dataTypeOptions: new DataTableCellDateTypeOptionsDataObject({
								outputFormat: getAppLocaleDatetimeFormat(
									LOCALE_DATE_FORMAT_NAME.SHORT,
									LOCALE_TIME_FORMAT_NAME.STANDARD
								),
							}),
							defaultValue: '—',
							emptyAsDefault: true,
							width: 1,
						},
						
						
						{
							dataType: DATA_TABLE_CELL_TYPE.ANY,
							dataTypeOptions: new DataTableCellAnyTypeOptionsDataObject({
								standardWrapper: false,
								content: this.renderActions,
							}),
							stopPropagation: true,
							width: 1,
						}
					]}
					onRowClick={data => this.redirectToItem(data.messageId)}
					data={mainList}
					paginationType={PAGINATION_TYPE.DYNAMIC}
					onSortByColumn={this.sortMainList}
					onPaginationClick={this.loadMainListPage}
					{...mainListPagination}
					{...mainListSort}
				/>
			</div>
		), (!singleProject ? 'no-project-selector' : undefined), undefined, {
			app: appConfig,
			mainSidebarShrank,
			toggleMainSidebarSizeAction,
		});
	}
}

export * from "./config";
export default connect(mapStateToProps, getPageActions(actions))(MessagesPage);